import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import Header from "./components/header";
import Main from "./components/main";
import QuranResearchDialog from "./components/QuranResearchDialog";
import { GITHUB, getVisionKey } from "./constants";
import { exportFolderToEpub, fetchReadmeMarkdownForRendering } from "./utils/epub-export";
import {
  isGithubAuthConfigured,
  onGithubAuthChanged,
  signInWithGithub,
  signOutGithub,
} from "./utils/github-auth";
import { checkReflectionsEditorIdentity } from "./utils/reflections-editor-access";
import { isPathAccessible } from "./utils/private-folder-access";
import { TtsProvider } from "./context/TtsContext";

const DEFAULT_COPY_LIMIT = 3500;
const GITHUB_API_BASE = "https://api.github.com";
const GITHUB_REPO_OWNER = "alialiayman";
const GITHUB_REPO_NAME = "reflections";
const GITHUB_ACCESS_TOKEN_STORAGE_KEY = "reflections_github_access_token";
const GITHUB_LOGIN_STORAGE_KEY = "reflections_github_login";

const FALLBACK_IMAGE_NAME = "صورة";

const safelyDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getNormalizedPathSegments = (pathname) =>
  pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => safelyDecodeURIComponent(segment).trim())
    .filter(Boolean);

const getFileNameParts = (fileName = "") => {
  const clean = fileName.trim();
  if (!clean) {
    return { extension: ".png", stem: FALLBACK_IMAGE_NAME };
  }

  const dotIndex = clean.lastIndexOf(".");
  if (dotIndex <= 0) {
    return { extension: ".png", stem: clean };
  }

  return {
    extension: clean.slice(dotIndex),
    stem: clean.slice(0, dotIndex),
  };
};

/**
 * Splits file stem like "2-1 عنوان الصورة" → prefix "2-1", base "عنوان الصورة".
 * Also handles "12 عنوان", "2-2-3 عنوان" (digits separated by hyphens), or no prefix.
 */
const splitNumericPrefix = (stem = "") => {
  const t = stem.trim();
  if (!t) {
    return { numericPrefix: "", baseName: FALLBACK_IMAGE_NAME };
  }

  const match = t.match(/^((?:\d+)(?:-\d+)*)(?:\s+(.+))?$/);
  if (!match) {
    return { numericPrefix: "", baseName: t || FALLBACK_IMAGE_NAME };
  }

  const numericPrefix = match[1];
  const rest = (match[2] || "").trim();
  return {
    numericPrefix,
    baseName: rest || FALLBACK_IMAGE_NAME,
  };
};

/** Keep only digits and single hyphens for the prefix field (e.g. 2-1, 12-3). */
const sanitizeImageNumericPrefixInput = (raw = "") =>
  raw
    .replace(/[^\d-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+/, "");

const extractSuggestedArabicName = (text = "") => {
  const match = text.match(/^\s*اسم\s+مقترح\s*:\s*(.+)$/im);
  if (!match) {
    return "";
  }

  return match[1]
    .replace(/_/g, " ")
    .replace(/^['"\s]+|['"\s]+$/g, "")
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const toEncodedGitHubContentsPath = (repoPath = "") =>
  repoPath
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");

const normalizeGithubContentBase64 = (raw) =>
  typeof raw === "string" ? raw.replace(/\s+/g, "") : "";

/**
 * Detect base64 that decodes to GitHub JSON metadata (would corrupt binary files if uploaded).
 */
const base64DecodesToGithubMetadataJson = (b64) => {
  if (!b64 || typeof b64 !== "string") {
    return false;
  }
  const chunk = b64.slice(0, 240);
  let decoded = "";
  try {
    decoded = window.atob(chunk);
  } catch {
    return false;
  }
  const t = decoded.trimStart();
  return (
    t.startsWith("{") &&
    (t.includes('"encoding"') || t.includes('"download_url"')) &&
    t.includes('"sha"')
  );
};

/**
 * Loads file bytes as base64 for Contents API PUT.
 * - Small files: inline `content` on the metadata response.
 * - Large files (GitHub omits `content`, encoding "none"): use **Git Blobs API** — the old
 *   `Accept: application/vnd.github.raw` on /contents/ often returns JSON metadata in the browser,
 *   which was accidentally uploaded and destroyed PNGs.
 *
 * @param {object} fileMetadata - GitHub GET /contents/{path} JSON (must include `sha` for blobs)
 */
const getGithubContentsFileBase64 = async (fileMetadata, token) => {
  const fromField = normalizeGithubContentBase64(fileMetadata?.content);
  if (fromField) {
    if (base64DecodesToGithubMetadataJson(fromField)) {
      throw new Error(
        "Refusing to use inline content: it looks like API metadata, not file bytes. Use blob API."
      );
    }
    return fromField;
  }
  const blobSha = fileMetadata?.sha;
  if (!token || !blobSha) {
    throw new Error(
      "GitHub returned no inline content and no blob SHA (cannot load large file for rename)."
    );
  }
  try {
    const blobUrl = `${GITHUB_API_BASE}/repos/${GITHUB_REPO_OWNER}/${GITHUB_REPO_NAME}/git/blobs/${blobSha}`;
    const res = await axios.get(blobUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/vnd.github+json",
      },
    });
    const b64 = normalizeGithubContentBase64(res.data?.content);
    if (!b64 || res.data?.encoding !== "base64") {
      throw new Error("GitHub blob response missing base64 body.");
    }
    if (base64DecodesToGithubMetadataJson(b64)) {
      throw new Error("Blob payload looks like JSON metadata, not binary file data.");
    }
    return b64;
  } catch (e) {
    if (e instanceof Error) {
      const m = e.message;
      if (
        m.startsWith("Refusing") ||
        m.includes("GitHub blob response") ||
        m.includes("inline content:") ||
        m.includes("Blob payload looks like")
      ) {
        throw e;
      }
    }
    const detail = formatGithubRequestError(e);
    throw new Error(
      detail || (e instanceof Error ? e.message : "") || "Could not load file bytes from GitHub (git blob API)."
    );
  }
};

const formatGithubRequestError = (error) => {
  const data = error?.response?.data;
  if (typeof data?.message === "string") {
    return data.message;
  }
  if (Array.isArray(data?.errors)) {
    return data.errors
      .map((e) => (typeof e?.message === "string" ? e.message : JSON.stringify(e)))
      .filter(Boolean)
      .join("; ");
  }
  if (error?.message) {
    return error.message;
  }
  return "";
};

/** TextFields on dark modal (#0f0f23): readable labels, inputs, borders */
const imageModalFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#f0f4f8",
    backgroundColor: "rgba(255,255,255,0.07)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.38)" },
    "&:hover fieldset": { borderColor: "rgba(0, 191, 166, 0.65)" },
    "&.Mui-focused fieldset": { borderColor: "#00BFA6", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.78)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#7ee8d4",
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.55)",
  },
};

function getCopyLimitFromQuery() {
  const query = window.location.search;
  const match = query.match(/\d+/); // Find the first number in the query string
  return match ? parseInt(match[0], 10) : DEFAULT_COPY_LIMIT;
}

const COPY_LIMIT = getCopyLimitFromQuery();
const REFERENCE_LINK = "\nhttps://a-reflections.web.app";
const QURAN_TRANSLATION_DISCLAIMER =
  "> **Quranic translation note:** Any English rendering here is a meaning-based interpretation for readability, not a verbatim or authoritative tafsir translation. For precise theological study, refer to classical Arabic tafsir and multiple trusted translations.";
const YOUTUBE_COMMENT_MAX_CHARS = 1200;

const LANGUAGE_OPTIONS = [
  { code: "ar", label: "Arabic (original)", openaiLabel: "Arabic", epubLanguage: "ar" },
  { code: "en", label: "English", openaiLabel: "English", epubLanguage: "en" },
  { code: "fr", label: "French", openaiLabel: "French", epubLanguage: "fr" },
  { code: "es", label: "Spanish", openaiLabel: "Spanish", epubLanguage: "es" },
  { code: "de", label: "German", openaiLabel: "German", epubLanguage: "de" },
  { code: "pt", label: "Portuguese", openaiLabel: "Portuguese", epubLanguage: "pt" },
  { code: "tr", label: "Turkish", openaiLabel: "Turkish", epubLanguage: "tr" },
  { code: "id", label: "Indonesian", openaiLabel: "Indonesian", epubLanguage: "id" },
  { code: "ur", label: "Urdu", openaiLabel: "Urdu", epubLanguage: "ur" },
  { code: "ru", label: "Russian", openaiLabel: "Russian", epubLanguage: "ru" },
  { code: "zh", label: "Chinese (Simplified)", openaiLabel: "Simplified Chinese", epubLanguage: "zh" },
  { code: "hi", label: "Hindi", openaiLabel: "Hindi", epubLanguage: "hi" },
];

const getLanguageMeta = (code) =>
  LANGUAGE_OPTIONS.find((l) => l.code === code) || LANGUAGE_OPTIONS[0];

const RTL_LANGUAGE_CODES = new Set(["ar"]);

function App() {
  const readmeSectionMarkdownsRef = useRef([]);
  const [loadingChunks, setLoadingChunks] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copyIndex, setCopyIndex] = useState(0);
  const [textChunks, setTextChunks] = useState([]);
  const [images, setImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageName, setSelectedImageName] = useState("");
  const [previewMode, setPreviewMode] = useState(true);
  const [imageDescription, setImageDescription] = useState("");
  const [describingImage, setDescribingImage] = useState(false);
  const [borderColorIndex, setBorderColorIndex] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [userQuestion, setUserQuestion] = useState("");
  const [askingQuestion, setAskingQuestion] = useState(false);
  const [imageBase64Data, setImageBase64Data] = useState(null);
  const [imageNumericPrefix, setImageNumericPrefix] = useState("");
  const [editableImageName, setEditableImageName] = useState(FALLBACK_IMAGE_NAME);
  const [selectedImageExtension, setSelectedImageExtension] = useState(".png");
  const [renamingImage, setRenamingImage] = useState(false);
  const [githubToken, setGithubToken] = useState(
    () => localStorage.getItem(GITHUB_ACCESS_TOKEN_STORAGE_KEY) || ""
  );
  const [githubLogin, setGithubLogin] = useState(
    () => localStorage.getItem(GITHUB_LOGIN_STORAGE_KEY) || ""
  );
  const [oauthConfigured] = useState(() => isGithubAuthConfigured());
  /** Push on repo + allowlisted user or org member (hajonsoft / alialiayman) */
  const [canEditReflections, setCanEditReflections] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(false);


  const borderColors = [
    "#6C63FF",
    "#00BFA6",
    "#FF6F61",
    "#FFD600",
    "#448AFF",
    "#E040FB",
    "#FF9100",
  ];
  const [copyToast, setCopyToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [quranResearchOpen, setQuranResearchOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("ar");
  const [translatedMarkdown, setTranslatedMarkdown] = useState("");
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationImageCaptionMap, setTranslationImageCaptionMap] = useState({});
  const [youtubeSummary, setYoutubeSummary] = useState("");
  const [youtubeSummaryOpen, setYoutubeSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [sourceMarkdownCache, setSourceMarkdownCache] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const contentDirection = RTL_LANGUAGE_CODES.has(selectedLanguage) ? "rtl" : "ltr";
  const path = window.location.pathname;
  const normalizedPathSegments = getNormalizedPathSegments(path);
  const apiPath = normalizedPathSegments
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  useEffect(() => {
    if (!oauthConfigured) {
      return () => {};
    }

    return onGithubAuthChanged((user) => {
      if (!user) {
        return;
      }

      if (user?.reloadUserInfo?.screenName) {
        const login = user.reloadUserInfo.screenName;
        setGithubLogin(login);
        localStorage.setItem(GITHUB_LOGIN_STORAGE_KEY, login);
      } else if (user?.providerData?.[0]?.uid) {
        const login = user.providerData[0].uid;
        setGithubLogin(login);
        localStorage.setItem(GITHUB_LOGIN_STORAGE_KEY, login);
      }
    });
  }, [oauthConfigured]);

  useEffect(() => {
    if (!githubToken) {
      setCanEditReflections(false);
      setAuthChecking(false);
      return;
    }

    let cancelled = false;
    const verifyRepoAccess = async () => {
      setAuthChecking(true);

      try {
        const identity = await checkReflectionsEditorIdentity(githubToken);
        if (!cancelled && identity.login) {
          setGithubLogin(identity.login);
          localStorage.setItem(GITHUB_LOGIN_STORAGE_KEY, identity.login);
        }

        const response = await axios.get(
          `${GITHUB_API_BASE}/repos/${GITHUB_REPO_OWNER}/${GITHUB_REPO_NAME}`,
          {
            headers: {
              Authorization: `Bearer ${githubToken}`,
              Accept: "application/vnd.github+json",
            },
          }
        );

        const canPush = Boolean(response.data?.permissions?.push);
        let mayEdit = false;

        if (canPush) {
          mayEdit = identity.eligible;
          if (!cancelled && !identity.eligible) {
            if (identity.reason === "read_org_scope_required") {
              setCopyToast({
                open: true,
                message:
                  "Sign out and sign in again with GitHub so the app can verify organization membership (read:org).",
                severity: "warning",
              });
            } else if (identity.login) {
              setCopyToast({
                open: true,
                message: `Signed in as @${identity.login}. Editing and AI features are limited to the repository owner, @hajonsoft, and members of the hajonsoft or alialiayman organizations.`,
                severity: "warning",
              });
            }
          }
        }

        if (!cancelled) {
          setCanEditReflections(canPush && mayEdit);
        }

        if (!canPush && !cancelled) {
          setCopyToast({
            open: true,
            message:
              "GitHub token is valid but does not have write access to this repository.",
            severity: "warning",
          });
        }
      } catch {
        if (!cancelled) {
          setCanEditReflections(false);
          localStorage.removeItem(GITHUB_ACCESS_TOKEN_STORAGE_KEY);
          setGithubToken("");
          setCopyToast({
            open: true,
            message: "GitHub authentication failed. Please sign in again.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setAuthChecking(false);
        }
      }
    };

    verifyRepoAccess();
    return () => {
      cancelled = true;
    };
  }, [githubToken]);

  const refreshImages = useCallback(async () => {
    if (!isPathAccessible(path, githubLogin)) {
      setImages([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.github.com/repos/alialiayman/reflections/contents/${apiPath}?ref=main`
      );
      const data = await response.json();

      if (Array.isArray(data)) {
        const imageFiles = data
          .filter((file) => {
            const lowerName = file.name.toLowerCase();
            return (
              lowerName.endsWith(".jpg") ||
              lowerName.endsWith(".jpeg") ||
              lowerName.endsWith(".png")
            );
          })
          .map((file) => ({
            name: file.name,
            url: file.download_url,
          }));

        setImages(imageFiles);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error("Failed to fetch images", error);
    }
  }, [apiPath, githubLogin, path]);

  useEffect(() => {
    void refreshImages();
  }, [refreshImages]);

  useEffect(() => {
    setSourceMarkdownCache("");
    setTranslatedMarkdown("");
    setTranslationImageCaptionMap({});
    setSelectedLanguage("ar");
  }, [path]);

  useEffect(() => {
    const readmeDiv = document.getElementById("readme");
    if (!readmeDiv) return;
    // MutationObserver to wait until content is loaded inside #readme
    const observer = new MutationObserver(() => {
      const fullText = readmeDiv.innerText.trim();
      if (fullText.length > COPY_LIMIT / 2) {
        const chunks = splitTextIntoChunks(fullText);
        setTextChunks(chunks);
        setLoadingChunks(false); // ✅ Set as ready
        observer.disconnect(); // Stop observing once text is ready
      }
    });
    observer.observe(readmeDiv, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const splitTextIntoChunks = (text) => {
    const chunks = [];
    let position = 0;
    let count = 1;
    while (position < text.length) {
      let chunk = text.substring(position, position + COPY_LIMIT);
      chunk += REFERENCE_LINK; // Add reference link at the end
      chunks.push(
        `(${count}/${Math.ceil(text.length / COPY_LIMIT)})\n\n` + chunk
      );
      position += COPY_LIMIT;
      count++;
    }
    return chunks;
  };

  const getCurrentVisibleChunks = () => {
    const readmeDiv = document.getElementById("readme");
    const fullText = readmeDiv?.innerText?.trim() || "";

    if (!fullText) {
      return [];
    }

    return splitTextIntoChunks(fullText);
  };

  const copyWithFallback = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copiedUsingFallback = document.execCommand("copy");
    document.body.removeChild(textArea);

    if (!copiedUsingFallback) {
      throw new Error("Clipboard is unavailable.");
    }
  };

  const handleCopy = async () => {
    const visibleChunks = getCurrentVisibleChunks();
    setTextChunks(visibleChunks);

    if (visibleChunks.length === 0) {
      setCopyToast({
        open: true,
        message: "No content to copy",
        severity: "warning",
      });
      return;
    }

    const currentIndex = copyIndex % visibleChunks.length;

    try {
      await copyWithFallback(visibleChunks[currentIndex]);
      const copiedChunkNumber = currentIndex + 1;
      const completedAllChunks = copiedChunkNumber === visibleChunks.length;

      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      setCopyIndex(completedAllChunks ? 0 : copiedChunkNumber);
      setCopyToast({
        open: true,
        message: completedAllChunks
          ? `Copied ${copiedChunkNumber}/${visibleChunks.length}. All parts copied.`
          : `Copied ${copiedChunkNumber}/${visibleChunks.length}.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to copy chunked text:", error);
      setCopyToast({
        open: true,
        message: "Failed to copy text. Please allow clipboard access.",
        severity: "error",
      });
    }
  };

  const handleCloseCopyToast = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setCopyToast((current) => ({ ...current, open: false }));
  };

  const handleSignInGithub = async () => {
    if (!oauthConfigured) {
      setCopyToast({
        open: true,
        message:
          "GitHub OAuth is not configured yet. Add Firebase env vars to enable sign-in.",
        severity: "warning",
      });
      return;
    }

    try {
      setAuthLoading(true);
      const { accessToken } = await signInWithGithub();

      if (!accessToken) {
        throw new Error("Missing GitHub access token.");
      }

      localStorage.setItem(GITHUB_ACCESS_TOKEN_STORAGE_KEY, accessToken);
      setGithubToken(accessToken);

      const identity = await checkReflectionsEditorIdentity(accessToken);
      if (identity.login) {
        setGithubLogin(identity.login);
        localStorage.setItem(GITHUB_LOGIN_STORAGE_KEY, identity.login);
      }

      setCopyToast({
        open: true,
        message: identity.login
          ? `Signed in with GitHub as @${identity.login}.`
          : "Signed in with GitHub.",
        severity: "success",
      });
    } catch {
      setCopyToast({
        open: true,
        message: "GitHub sign-in failed. Please try again.",
        severity: "error",
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOutGithub = async () => {
    await signOutGithub();
    localStorage.removeItem(GITHUB_ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(GITHUB_LOGIN_STORAGE_KEY);
    setGithubToken("");
    setGithubLogin("");
    setCanEditReflections(false);
    setCopyToast({
      open: true,
      message: "Signed out from GitHub editor mode.",
      severity: "info",
    });
  };

  
  const handleClickOpen = (imageUrl, imageName) => {
    setSelectedImage(imageUrl);
    setSelectedImageName(imageName || "");
    const { extension, stem } = getFileNameParts(imageName || "");
    const { numericPrefix, baseName } = splitNumericPrefix(stem);
    setSelectedImageExtension(extension || ".png");
    setImageNumericPrefix(numericPrefix);
    setEditableImageName(baseName);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
    setSelectedImageName("");
    setImageDescription("");
    setChatMessages([]);
    setUserQuestion("");
    setImageBase64Data(null);
    setImageNumericPrefix("");
    setEditableImageName(FALLBACK_IMAGE_NAME);
    setSelectedImageExtension(".png");
  };

  const handleDescribeImage = async () => {
    if (!selectedImage) return;
    if (!canEditReflections || !githubToken) {
      setCopyToast({
        open: true,
        message:
          "Describe and AI image features are only available to authorized editors (repository access + allowlisted account or org).",
        severity: "warning",
      });
      return;
    }
    setDescribingImage(true);
    setImageDescription("");
    try {
      const imgResponse = await fetch(selectedImage);
      const blob = await imgResponse.blob();
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(blob);
      });

      const mimeType = blob.type || "image/png";
      setImageBase64Data({ base64, mimeType });
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getVisionKey()}`,
        },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: [
            {
              role: "user",
              content: [
                { type: "text", text: `You are an expert in ancient Egyptian hieroglyphs, Egyptology, and Quranic studies. Describe this image in detail in Arabic.

If the image contains hieroglyphs:
- Act as an expert hieroglyph reader. Carefully read and transliterate every hieroglyphic symbol visible.
- If cartouches are detected, read and translate the names inside them.
- Explain what is written in the hieroglyphs to someone who wants to understand the text.
- The word "nṯr" (netjer) in hieroglyphs represents a FORCE or power, NOT a god. Ancient Egyptians did not believe in gods as deities — they understood these figures as forces and archetypes within the human psyche.

Use the following correspondences between ancient/historical figures and Quranic/religious figures when analyzing the image:
- Imhotep (إمحوتب) = Prophet Joseph (يوسف)
- Nut (نوت) the sky goddess = Noah (نوح)
- Thutmose III (تحتمس الثالث) = Prophet Solomon (سليمان)
- Thutmose II (تحتمس الثاني) = Prophet David (داود)
- Moses (موسى) = Zarathustra/Zoroaster (زرادشت)
- Abraham (إبراهيم) = Socrates (سقراط)
- Plato (أفلاطون) = Ismail (إسماعيل)
- Aristotle (أرسطو) = Isaac (إسحاق)
- Jacob (يعقوب) is NOT Israel
- Lot (لوط) = Buddha (بوذا)
- Mohamed (محمد) = Mani (ماني)
- Akhenaten/Ikhnaton (إخناتون) = Imran (عمران)
- Tutankhamun (توت عنخ آمون) = Yahia/John (يحيى)
- Meritaten (ميريت آتون) = Mother Mary (السيدة مريم)
- Smenkhkare (سمنخ كع رع) = Zakaria (زكريا)

When relevant, explain the image from a Quranic perspective using these correspondences.

Then on a new line prefixed with 'اسم مقترح: ' suggest an Arabic file name for this image (less than 15 words, Arabic letters only, no numbers, no extension, no English words). Finally, add a line of relevant hashtags in English.` },
                {
                  type: "image_url",
                  image_url: {
                    url: `data:${mimeType};base64,${base64}`,
                  },
                },
              ],
            },
          ],
          max_tokens: 2000,
        }),
      });

      const data = await res.json();
      const content = data.choices?.[0]?.message?.content || "No description returned.";
      const suggestedArabicName = extractSuggestedArabicName(content);
      if (suggestedArabicName) {
        setEditableImageName(suggestedArabicName);
      }
      setImageDescription(content);
      setChatMessages([
        { role: "assistant", content },
      ]);
      setBorderColorIndex((prev) => (prev + 1) % borderColors.length);
    } catch (err) {
      console.error("Failed to describe image:", err);
      setImageDescription("Error: could not describe image.");
    } finally {
      setDescribingImage(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!userQuestion.trim() || !imageBase64Data) return;
    if (!canEditReflections || !githubToken) {
      setCopyToast({
        open: true,
        message:
          "Follow-up questions require an authorized editor sign-in (repository access + allowlisted account or org).",
        severity: "warning",
      });
      return;
    }
    const question = userQuestion.trim();
    setUserQuestion("");
    setAskingQuestion(true);

    const newMessages = [...chatMessages, { role: "user", content: question }];
    setChatMessages(newMessages);

    try {
      const apiMessages = [
        {
          role: "user",
          content: [
            { type: "text", text: "This is an image of an ancient Egyptian artifact. Answer all questions in Arabic." },
            {
              type: "image_url",
              image_url: {
                url: `data:${imageBase64Data.mimeType};base64,${imageBase64Data.base64}`,
              },
            },
          ],
        },
        ...newMessages.map((m) => ({ role: m.role, content: m.content })),
      ];

      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getVisionKey()}`,
        },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: apiMessages,
          max_tokens: 2000,
        }),
      });

      const data = await res.json();
      const reply = data.choices?.[0]?.message?.content || "No response.";
      setChatMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      setBorderColorIndex((prev) => (prev + 1) % borderColors.length);
    } catch (err) {
      console.error("Failed to ask question:", err);
      setChatMessages((prev) => [...prev, { role: "assistant", content: "حدث خطأ أثناء الإجابة." }]);
    } finally {
      setAskingQuestion(false);
    }
  };

  const handleRenameImageOnGithub = async () => {
    if (!canEditReflections || !githubToken) {
      setCopyToast({
        open: true,
        message:
          "Renaming images requires an authorized editor (push access plus allowlisted GitHub user or hajonsoft / alialiayman org member).",
        severity: "warning",
      });
      return;
    }

    if (!selectedImageName) {
      setCopyToast({
        open: true,
        message: "No selected image to rename.",
        severity: "warning",
      });
      return;
    }

    const currentName = selectedImageName.trim();
    const targetName = suggestedFullImageName.trim();
    if (!targetName) {
      setCopyToast({
        open: true,
        message: "Target image name cannot be empty.",
        severity: "warning",
      });
      return;
    }

    if (currentName === targetName) {
      setCopyToast({
        open: true,
        message: "The new image name is the same as the current one.",
        severity: "info",
      });
      return;
    }

    const currentRepoPath = [...normalizedPathSegments, currentName].join("/");
    const targetRepoPath = [...normalizedPathSegments, targetName].join("/");
    const currentContentsUrl = `${GITHUB_API_BASE}/repos/${GITHUB_REPO_OWNER}/${GITHUB_REPO_NAME}/contents/${toEncodedGitHubContentsPath(currentRepoPath)}`;
    const targetContentsUrl = `${GITHUB_API_BASE}/repos/${GITHUB_REPO_OWNER}/${GITHUB_REPO_NAME}/contents/${toEncodedGitHubContentsPath(targetRepoPath)}`;

    setRenamingImage(true);
    try {
      const fileResponse = await axios.get(`${currentContentsUrl}?ref=main`, {
        headers: {
          Authorization: `Bearer ${githubToken}`,
          Accept: "application/vnd.github+json",
        },
      });

      const fileSha = fileResponse.data?.sha;
      if (!fileSha) {
        throw new Error("Could not read existing file metadata (missing SHA).");
      }

      let base64Content;
      try {
        base64Content = await getGithubContentsFileBase64(fileResponse.data, githubToken);
      } catch (readErr) {
        throw new Error(
          readErr instanceof Error ? readErr.message : "Could not read image bytes from GitHub."
        );
      }
      if (!base64Content) {
        throw new Error("Image content was empty after loading from GitHub.");
      }

      await axios.put(
        targetContentsUrl,
        {
          message: `Rename image: ${currentName} -> ${targetName}`,
          content: base64Content,
          encoding: "base64",
          branch: "main",
        },
        {
          headers: {
            Authorization: `Bearer ${githubToken}`,
            Accept: "application/vnd.github+json",
          },
        }
      );

      await axios.delete(currentContentsUrl, {
        headers: {
          Authorization: `Bearer ${githubToken}`,
          Accept: "application/vnd.github+json",
        },
        data: {
          message: `Delete old image after rename: ${currentName}`,
          sha: fileSha,
          branch: "main",
        },
      });

      const updatedImageUrl = `${GITHUB}/${toEncodedGitHubContentsPath(targetRepoPath)}?v=${Date.now()}`;
      setSelectedImageName(targetName);
      setSelectedImage(updatedImageUrl);
      {
        const { extension: ext, stem } = getFileNameParts(targetName);
        const { numericPrefix, baseName } = splitNumericPrefix(stem);
        setSelectedImageExtension(ext || ".png");
        setImageNumericPrefix(numericPrefix);
        setEditableImageName(baseName);
      }
      setImages((previous) =>
        previous.map((image) =>
          image.name === currentName
            ? { ...image, name: targetName, url: updatedImageUrl }
            : image
        )
      );

      setCopyToast({
        open: true,
        message: `Image renamed to ${targetName}`,
        severity: "success",
      });
    } catch (error) {
      const detail = formatGithubRequestError(error);
      setCopyToast({
        open: true,
        message: detail ? `Rename failed: ${detail}` : "Failed to rename image on GitHub.",
        severity: "error",
      });
    } finally {
      setRenamingImage(false);
    }
  };

  const normalizedNumericPrefix = sanitizeImageNumericPrefixInput(imageNumericPrefix).replace(
    /-+$/,
    ""
  );
  const normalizedImageBaseName = (editableImageName || FALLBACK_IMAGE_NAME)
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim() || FALLBACK_IMAGE_NAME;
  const suggestedFullImageName = normalizedNumericPrefix
    ? `${normalizedNumericPrefix} ${normalizedImageBaseName}${selectedImageExtension || ".png"}`
    : `${normalizedImageBaseName}${selectedImageExtension || ".png"}`;

  const handleDownloadFullReadmeMarkdown = () => {
    if (previewMode) {
      setCopyToast({
        open: true,
        message: "Switch to README view to download markdown.",
        severity: "info",
      });
      return;
    }
    const parts = readmeSectionMarkdownsRef.current;
    const text = Array.isArray(parts) ? parts.join("\n\n") : "";
    if (!text.trim()) {
      setCopyToast({
        open: true,
        message: "No README content loaded yet.",
        severity: "warning",
      });
      return;
    }
    const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const folderLabel =
      normalizedPathSegments.length > 0
        ? normalizedPathSegments[normalizedPathSegments.length - 1]
        : "root";
    const safeStem =
      folderLabel.replace(/[^\w\u0600-\u06FF.-]+/g, "_").replace(/^_|_$/g, "") || "README";
    a.href = url;
    a.download = `${safeStem}-README.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setCopyToast({
      open: true,
      message: "README markdown downloaded.",
      severity: "success",
    });
  };

  const loadSourceMarkdown = async () => {
    if (!isPathAccessible(path, githubLogin)) {
      throw new Error("This page is not available.");
    }
    if (sourceMarkdownCache.trim()) {
      return sourceMarkdownCache;
    }
    const markdown = await fetchReadmeMarkdownForRendering({ path, githubLogin });
    setSourceMarkdownCache(markdown);
    return markdown;
  };

  const buildTranslationPrompt = (targetLanguageLabel) => `
Translate the following Arabic README markdown into ${targetLanguageLabel}.

Constraints:
- Output valid markdown only (no code fences, no explanations).
- Keep all markdown structure (headings, lists, tables, links).
- Keep image markdown paths unchanged.
- Style: academic, clear, and engaging like a high-quality book.
- For Quranic text:
  1) Keep the original Arabic Quranic text line(s).
  2) Add a meaning-based translation (not literal word-by-word).
  3) Keep the intended meaning and avoid over-assertive theological interpretation.
  4) Include this disclaimer exactly once near the beginning of the document:
${QURAN_TRANSLATION_DISCLAIMER}

Markdown:
`.trim();

  const buildCaptionTranslationPrompt = (targetLanguageLabel) => `
Translate these image captions into ${targetLanguageLabel}.
Return JSON only with shape: {"captions":[{"source":"...","translated":"..."}]}
Rules:
- Keep concise, human, book-ready caption style.
- Do not include numbering prefixes; source strings already exclude numbers.
- No extra keys, no commentary.
`.trim();

  const translateMarkdownWithOpenAi = async (markdown, languageCode) => {
    if (languageCode === "ar") {
      return markdown;
    }
    const lang = getLanguageMeta(languageCode);
    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getVisionKey()}`,
      },
      body: JSON.stringify({
        model: "gpt-4o",
        temperature: 0.35,
        max_tokens: 16000,
        messages: [
          { role: "system", content: "You are an expert literary translator for Arabic nonfiction books." },
          { role: "user", content: `${buildTranslationPrompt(lang.openaiLabel)}\n\n${markdown}` },
        ],
      }),
    });
    const data = await res.json();
    const translated = data?.choices?.[0]?.message?.content?.trim();
    if (!res.ok || !translated) {
      throw new Error(data?.error?.message || "Translation failed.");
    }
    return translated;
  };

  const translateImageCaptionsWithOpenAi = async (languageCode) => {
    if (languageCode === "ar") {
      return {};
    }
    const captions = images
      .map((img) => {
        const stem = getFileNameParts(img.name).stem;
        return splitNumericPrefix(stem).baseName || FALLBACK_IMAGE_NAME;
      })
      .filter(Boolean);
    if (!captions.length) {
      return {};
    }
    const lang = getLanguageMeta(languageCode);
    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getVisionKey()}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        temperature: 0.2,
        response_format: { type: "json_object" },
        messages: [
          { role: "system", content: "You return strict JSON only." },
          {
            role: "user",
            content: `${buildCaptionTranslationPrompt(lang.openaiLabel)}\n\n${JSON.stringify(captions)}`,
          },
        ],
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error?.message || "Caption translation failed.");
    }
    const raw = data?.choices?.[0]?.message?.content || "{}";
    let parsed = {};
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
    const map = {};
    const rows = Array.isArray(parsed?.captions) ? parsed.captions : [];
    rows.forEach((row) => {
      const src = String(row?.source || "").trim();
      const tr = String(row?.translated || "").trim();
      if (src && tr) {
        map[src] = tr;
      }
    });
    return map;
  };

  const applyLanguage = async (languageCode) => {
    const previousLanguage = selectedLanguage;
    setSelectedLanguage(languageCode);
    if (languageCode === "ar") {
      setTranslatedMarkdown("");
      setTranslationImageCaptionMap({});
      return;
    }
    setTranslationLoading(true);
    try {
      const source = await loadSourceMarkdown();
      const [translated, captionMap] = await Promise.all([
        translateMarkdownWithOpenAi(source, languageCode),
        translateImageCaptionsWithOpenAi(languageCode),
      ]);
      setTranslatedMarkdown(translated);
      setTranslationImageCaptionMap(captionMap);
      setCopyToast({
        open: true,
        message: `Translated to ${getLanguageMeta(languageCode).label}.`,
        severity: "success",
      });
    } catch (e) {
      setSelectedLanguage(previousLanguage);
      setCopyToast({
        open: true,
        message: e instanceof Error ? e.message : "Translation failed.",
        severity: "error",
      });
    } finally {
      setTranslationLoading(false);
    }
  };

  const handleExportEpubWithLanguage = async ({ path: exportPath, images: exportImages }) => {
    if (!isPathAccessible(exportPath, githubLogin)) {
      throw new Error("Unable to export this folder.");
    }
    const lang = getLanguageMeta(selectedLanguage);
    let sourceMarkdown = await loadSourceMarkdown();
    let captionMap = null;
    if (selectedLanguage !== "ar") {
      sourceMarkdown =
        translatedMarkdown || (await translateMarkdownWithOpenAi(sourceMarkdown, selectedLanguage));
      captionMap =
        Object.keys(translationImageCaptionMap || {}).length > 0
          ? translationImageCaptionMap
          : await translateImageCaptionsWithOpenAi(selectedLanguage);
    }
    await exportFolderToEpub({
      path: exportPath,
      images: exportImages,
      sourceMarkdown,
      language: lang.epubLanguage,
      imageCaptionMap: captionMap,
      githubLogin,
    });
  };

  const handleGenerateYoutubeSummary = async () => {
    setSummaryLoading(true);
    try {
      const lang = getLanguageMeta(selectedLanguage);
      const source =
        selectedLanguage === "ar"
          ? await loadSourceMarkdown()
          : translatedMarkdown || (await loadSourceMarkdown());
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getVisionKey()}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          temperature: 0.45,
          max_tokens: 1200,
          messages: [
            {
              role: "system",
              content:
                "You write concise, high-quality social comments. Return plain text only.",
            },
            {
              role: "user",
              content: `Summarize this article into ONE YouTube comment in ${lang.openaiLabel}.

Hard constraints:
- Max ${YOUTUBE_COMMENT_MAX_CHARS} characters total.
- Keep it a single concise paragraph (no bullets, no hashtags, no title).
- Preserve the core idea and key takeaway in engaging but natural language.
- Do not add facts not present in the article.

Article markdown:
${source}`,
            },
          ],
        }),
      });
      const data = await res.json();
      const summary = data?.choices?.[0]?.message?.content?.trim();
      if (!res.ok || !summary) {
        throw new Error(data?.error?.message || "Summary generation failed.");
      }
      const trimmed = summary.length > YOUTUBE_COMMENT_MAX_CHARS
        ? summary.slice(0, YOUTUBE_COMMENT_MAX_CHARS).trimEnd()
        : summary;
      setYoutubeSummary(trimmed);
      setYoutubeSummaryOpen(true);
    } catch (e) {
      setCopyToast({
        open: true,
        message: e instanceof Error ? e.message : "Summary generation failed.",
        severity: "error",
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <TtsProvider
      getApiKey={getVisionKey}
      enabled={canEditReflections}
      sectionMarkdownsRef={readmeSectionMarkdownsRef}
    >
    <>
      <Header
        id="web-header"
        handleCopy={handleCopy}
        copied={copied}
        copyIndex={copyIndex}
        textChunks={textChunks}
        images={images}
        handleClickOpen={handleClickOpen}
        loading={loadingChunks}
        previewMode={previewMode}
        onTogglePreview={() => setPreviewMode((current) => !current)}
        githubLogin={githubLogin}
        oauthConfigured={oauthConfigured}
        isGithubSignedIn={Boolean(githubToken)}
        canEditReflections={canEditReflections}
        authLoading={authLoading}
        authChecking={authChecking}
        onSignInGithub={handleSignInGithub}
        onSignOutGithub={handleSignOutGithub}
        onDownloadReadmeMarkdown={handleDownloadFullReadmeMarkdown}
        onOpenQuranResearch={() => setQuranResearchOpen(true)}
        selectedLanguage={selectedLanguage}
        languageOptions={LANGUAGE_OPTIONS}
        onChangeLanguage={applyLanguage}
        translationLoading={translationLoading}
        onGenerateYoutubeSummary={handleGenerateYoutubeSummary}
        summaryLoading={summaryLoading}
        onExportEpub={handleExportEpubWithLanguage}
        isEditing={isEditing}
      />
      <QuranResearchDialog
        open={quranResearchOpen}
        onClose={() => setQuranResearchOpen(false)}
        githubToken={githubToken}
        canEditReflections={canEditReflections}
        folderSegments={normalizedPathSegments}
        getOpenAiKey={getVisionKey}
        onNotify={({ message, severity }) =>
          setCopyToast({ open: true, message, severity: severity || "info" })
        }
      />
      <div id="print-header" style={{display: 'none'}}></div>
      <Container p={2} mt={2}>
        <Main
          previewMode={previewMode}
          images={images}
          githubToken={githubToken}
          githubLogin={githubLogin}
          canEditReflections={canEditReflections}
          sectionMarkdownsRef={readmeSectionMarkdownsRef}
          openImageModal={handleClickOpen}
          onImagesChanged={refreshImages}
          sourceMarkdown={
            selectedLanguage === "ar" || !translatedMarkdown ? null : translatedMarkdown
          }
          imageCaptionMap={selectedLanguage === "ar" ? null : translationImageCaptionMap}
          contentDirection={contentDirection}
          onEditingChange={setIsEditing}
        />
      </Container>

      <Dialog
        open={youtubeSummaryOpen}
        onClose={() => setYoutubeSummaryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            YouTube comment draft ({getLanguageMeta(selectedLanguage).label})
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
            {`${youtubeSummary.length}/${YOUTUBE_COMMENT_MAX_CHARS} characters`}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={10}
            value={youtubeSummary}
            InputProps={{ readOnly: true }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                navigator.clipboard?.writeText(youtubeSummary || "");
                setCopyToast({
                  open: true,
                  message: "Summary copied.",
                  severity: "success",
                });
              }}
            >
              Copy summary
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal for Enlarged Image */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={imageDescription ? "lg" : "md"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            background: "#1a1a2e",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: imageDescription ? "row" : "column" },
              minHeight: 300,
            }}
          >
            {/* Image side */}
            <Box
              sx={{
                flex: imageDescription ? "0 0 50%" : "1 1 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "#0f0f23",
                p: 2,
              }}
            >
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Large Illustration"
                  className="modal-image-glow"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "65vh",
                    objectFit: "contain",
                    borderRadius: 14,
                    border: "3px solid #00BFA6",
                    boxShadow: "0 0 25px rgba(0, 191, 166, 0.5), 0 0 60px rgba(0, 191, 166, 0.15), 0 10px 40px rgba(0, 0, 0, 0.6)",
                    animation: "imageReveal 0.6s ease-out",
                  }}
                />
              )}
              {/* Rename (always) + Describe / follow-up */}
              <Box
                sx={{
                  width: "100%",
                  mt: 1.5,
                  px: 1,
                  pb: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#e8ecf0",
                    fontWeight: 600,
                    mb: 0.75,
                    letterSpacing: "0.02em",
                  }}
                >
                  تسمية الملف على GitHub
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "rgba(255,255,255,0.65)",
                    mb: 1.25,
                  }}
                >
                  الحالي:{" "}
                  <Box component="span" sx={{ color: "#b8f5e8", wordBreak: "break-all" }}>
                    {selectedImageName || "—"}
                  </Box>
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "minmax(112px, 168px) 1fr" },
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <TextField
                    type="text"
                    label="التسلسل الرقمي"
                    placeholder="مثل 12 أو 2-1"
                    size="small"
                    value={imageNumericPrefix}
                    onChange={(event) => {
                      setImageNumericPrefix(sanitizeImageNumericPrefixInput(event.target.value));
                    }}
                    inputProps={{ inputMode: "text", "aria-label": "Numeric sequence prefix" }}
                    helperText="أرقام وشرطة فقط"
                    FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)", mx: 0 } }}
                    sx={imageModalFieldSx}
                  />
                  <TextField
                    label="الاسم (نص)"
                    size="small"
                    value={editableImageName}
                    onChange={(event) => setEditableImageName(event.target.value)}
                    placeholder={FALLBACK_IMAGE_NAME}
                    sx={imageModalFieldSx}
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    display: "block",
                    color: "#d4dde6",
                    mb: 1,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "0.8rem",
                    bgcolor: "rgba(0,0,0,0.35)",
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    border: "1px solid rgba(255,255,255,0.15)",
                  }}
                >
                  الاسم النهائي: {suggestedFullImageName}
                </Typography>

                <Button
                  onClick={handleRenameImageOnGithub}
                  variant="contained"
                  size="medium"
                  fullWidth
                  startIcon={
                    renamingImage ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DriveFileRenameOutlineIcon />
                    )
                  }
                  disabled={renamingImage || !canEditReflections || !githubToken}
                  sx={{
                    mb: 0.5,
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: "#0d7a66",
                    color: "#fff",
                    "&:hover": { bgcolor: "#0a6353" },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.45)",
                      bgcolor: "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  {renamingImage ? "جاري الحفظ…" : "حفظ التسمية على GitHub"}
                </Button>
                {(!githubToken || !canEditReflections) && (
                  <Typography variant="caption" sx={{ display: "block", color: "rgba(255,200,120,0.95)", mt: 0.5 }}>
                    {!githubToken
                      ? "سجّل الدخول عبر GitHub لتفعيل الحفظ على المستودع."
                      : "الحفظ والذكاء الاصطناعي للصور للمحرّرين المصرّح لهم فقط (صلاحية الدفع + حساب معتمد أو عضوية منظمة hajonsoft أو alialiayman). قد تحتاج لتسجيل الخروج ثم الدخول مرة أخرى بعد تحديث الصلاحيات."}
                  </Typography>
                )}

                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.14)" }} />

                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.6)", display: "block", mb: 1 }}
                >
                  الوصف بالذكاء الاصطناعي يملأ حقل «الاسم (نص)» فقط عند توفر «اسم مقترح» في الرد.
                </Typography>

                {!imageDescription ? (
                  describingImage ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      <CircularProgress size={28} sx={{ color: "#7ee8d4" }} />
                    </Box>
                  ) : canEditReflections && githubToken ? (
                    <Button
                      onClick={handleDescribeImage}
                      variant="outlined"
                      size="small"
                      startIcon={<AutoAwesomeIcon />}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        py: 0.75,
                        textTransform: "none",
                        color: "#e0e0e0",
                        borderColor: "rgba(255,255,255,0.35)",
                        "&:hover": {
                          borderColor: "#00BFA6",
                          backgroundColor: "rgba(0,191,166,0.12)",
                        },
                      }}
                    >
                      Describe
                    </Button>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(255,200,160,0.95)",
                        lineHeight: 1.5,
                        px: 0.5,
                      }}
                    >
                      {!githubToken
                        ? "سجّل الدخول عبر GitHub لاستخدام وصف الصورة بالذكاء الاصطناعي."
                        : "وصف الصورة بالذكاء الاصطناعي متاح فقط للمحرّرين المصرّح لهم على مستودع reflections (صلاحية الدفع + حساب معتمد أو عضوية منظمة hajonsoft أو alialiayman)."}
                    </Typography>
                  )
                ) : (
                  <TextField
                    value={userQuestion}
                    onChange={(e) => setUserQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAskQuestion();
                      }
                    }}
                    placeholder="اسأل عن الصورة..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={askingQuestion || !canEditReflections || !githubToken}
                    sx={imageModalFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {askingQuestion ? (
                            <CircularProgress size={22} sx={{ color: "#7ee8d4" }} />
                          ) : (
                            <IconButton
                              onClick={handleAskQuestion}
                              disabled={
                                !userQuestion.trim() || !canEditReflections || !githubToken
                              }
                              size="small"
                              sx={{ color: "#7ee8d4" }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Conversation side */}
            {chatMessages.length > 0 && (
              <Box
                sx={{
                  flex: "1 1 50%",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  overflow: "auto",
                  maxHeight: "75vh",
                  gap: 2,
                }}
              >
                {chatMessages.map((msg, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      border: msg.role === "assistant" ? 2 : 1,
                      borderColor: msg.role === "assistant"
                        ? borderColors[(borderColorIndex + idx) % borderColors.length]
                        : "#555",
                      borderRadius: 3,
                      p: 2,
                      transition: "border-color 0.5s ease",
                      background: msg.role === "assistant"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(108,99,255,0.08)",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#e0e0e0",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.8,
                        fontSize: "1.05rem",
                        fontFamily: '"Roboto", "Segoe UI", sans-serif',
                      }}
                    >
                      {msg.content}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copyToast.open}
        autoHideDuration={3000}
        onClose={handleCloseCopyToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseCopyToast}
          severity={copyToast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {copyToast.message}
        </Alert>
      </Snackbar>
    </>
    </TtsProvider>
  );
}

export default App;
